import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Pencil, Trash2, Plus, Bot, AlertTriangle } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import QueueIntegrationModal from "../../components/QueueIntegrationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const TIPOS = {
	typebot: "Typebot",
	n8n: "n8n",
	webhook: "Webhook próprio",
};

const QueueIntegrations = () => {
	const [integracoes, setIntegracoes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/queue-integrations");
			setIntegracoes(data);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const excluir = async (id) => {
		try {
			await api.delete(`/queue-integrations/${id}`);
			toast.success("Integração removida.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionada && `Remover a integração "${selecionada.name}"?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={() => excluir(selecionada.id)}
			>
				{selecionada?.queues?.length > 0
					? `Ela atende ${selecionada.queues.length} fila(s) hoje. Elas voltam a atender pelo menu, e quem estiver no meio de uma conversa com o bot passa a esperar atendimento humano.`
					: "Ela não está em nenhuma fila, então nada muda no atendimento."}
			</ConfirmationModal>

			<QueueIntegrationModal
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
					setSelecionada(null);
				}}
				integrationId={selecionada?.id}
				onSaved={carregar}
			/>

			<MainHeader>
				<Title>Integrações</Title>
				<MainHeaderButtonsWrapper>
					<Button
						onClick={() => {
							setSelecionada(null);
							setModalOpen(true);
						}}
					>
						<Plus className="h-4 w-4" />
						Nova integração
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-6 pb-6">
				<div className="mb-4 rounded-xl border bg-muted p-3 text-xs leading-relaxed">
					<p className="flex items-start gap-1.5 font-medium">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						Uma integração só entra em ação depois de ser escolhida numa fila.
					</p>
					<p className="mt-1.5 text-muted-foreground">
						Quando a fila do atendimento tem uma integração ativa, é o bot de fora
						que responde o cliente — antes da Inteligência Artificial e depois do
						menu da fila, se houver menu. Abra{" "}
						<span className="font-medium text-foreground">Filas</span> para ligar
						uma integração a uma fila.
					</p>
				</div>

				<div className="overflow-x-auto rounded-xl border bg-card">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Nome</TableHead>
								<TableHead>Tipo</TableHead>
								<TableHead>Onde está ligada</TableHead>
								<TableHead className="text-center">Situação</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{integracoes.map((integracao) => (
								<TableRow key={integracao.id}>
									<TableCell className="font-medium">
										<span className="flex items-center gap-2">
											<Bot className="h-4 w-4 text-muted-foreground" />
											{integracao.name}
										</span>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{TIPOS[integracao.type] || integracao.type}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{integracao.queues?.length
											? integracao.queues.map((f) => f.name).join(", ")
											: "Nenhuma fila — não atende ninguém ainda"}
									</TableCell>
									<TableCell className="text-center">
										{integracao.isActive ? (
											<Badge variant="secondary">Ativa</Badge>
										) : (
											<Badge variant="outline">Desligada</Badge>
										)}
									</TableCell>
									<TableCell>
										<div className="flex items-center justify-center gap-1">
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												aria-label="Editar integração"
												title="Editar integração"
												onClick={() => {
													setSelecionada(integracao);
													setModalOpen(true);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 text-destructive hover:text-destructive"
												aria-label="Remover integração"
												title="Remover integração"
												onClick={() => {
													setSelecionada(integracao);
													setConfirmOpen(true);
												}}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
							{loading && <TableRowSkeleton columns={5} />}
							{!loading && integracoes.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={5}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nenhuma integração ainda.
										<span className="mt-1 block text-xs">
											Use “Nova integração” para ligar um Typebot, um fluxo do
											n8n ou um serviço seu ao atendimento.
										</span>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</MainContainer>
	);
};

export default QueueIntegrations;
